import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import './AndroidPackagePanel.css';

const POLL_MS = 8000;

export function androidPackageLabel(state) {
  switch (state) {
    case 'QUEUED': return 'בתור לבנייה...';
    case 'RUNNING': return 'בונה את חבילת האנדרואיד (Gradle)...';
    case 'READY': return 'חבילת האנדרואיד מוכנה';
    case 'FAILED': return 'בניית חבילת האנדרואיד נכשלה';
    default: return 'אפשר לבנות קובץ התקנה לאנדרואיד מהאפליקציה המאומתת';
  }
}

// A clear, honest reason for the customer; the technical detail stays below it.
export function androidFailureHint(errorCode) {
  if (errorCode === 'ANDROID_DEPENDENCY_DOWNLOAD_FAILED') return 'הורדת כלי הבנייה נכשלה זמנית. האתר לא נפגע — אפשר לנסות שוב.';
  if (errorCode === 'EXECUTION_WATCHDOG_TIMEOUT' || errorCode === 'ANDROID_PACKAGE_TIMEOUT') return 'הבנייה לא הסתיימה בזמן ולכן נעצרה. האתר לא נפגע — אפשר לנסות שוב.';
  if (errorCode === 'ANDROID_TOOLCHAIN_FAILED') return 'תקלה בתשתית בניית האנדרואיד. האתר לא נפגע; הצוות קיבל את הפרטים.';
  return 'בניית האנדרואיד נכשלה. האתר לא נפגע — הפרטים מופיעים למטה.';
}

export function isAndroidTarget(target, targetPlatforms) {
  return target === 'MOBILE' && Array.isArray(targetPlatforms) && targetPlatforms.map(value => String(value).toUpperCase()).includes('ANDROID');
}

// A verified website can be wrapped into an Android app from the chat as well. The server
// decides finally (static web types only) and answers with a clear message otherwise.
export function canPackageAndroid(target, targetPlatforms) {
  return isAndroidTarget(target, targetPlatforms) || String(target || 'WEB').toUpperCase() === 'WEB';
}

/**
 * Explicit "build Android package" control for a verified MOBILE project. The Preview stays
 * exactly as it is; this only queues the Android worker and offers the verified download.
 */
export default function AndroidPackagePanel({ projectId, target, targetPlatforms }) {
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const timer = useRef(null);
  const enabled = Boolean(projectId) && canPackageAndroid(target, targetPlatforms);
  const fromWebsite = !isAndroidTarget(target, targetPlatforms);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await api.androidPackageStatus(projectId);
      setPkg(data.androidPackage || null);
    } catch (err) {
      setError(err.response?.data?.error || '');
    }
  }, [enabled, projectId]);

  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (pkg && (pkg.state === 'QUEUED' || pkg.state === 'RUNNING')) timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [pkg, load]);

  if (!enabled) return null;

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await api.requestAndroidPackage(projectId);
      setPkg(data.androidPackage || null);
    } catch (err) {
      setError(err.response?.data?.error || 'לא ניתן להתחיל את בניית חבילת האנדרואיד כרגע.');
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!pkg?.jobId) return;
    setDownloading(true);
    try {
      const response = await api.downloadVerifiedArtifact(pkg.jobId);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `android-package-${pkg.jobId}.tar.gz`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      setError('ההורדה נכשלה. נסה שוב.');
    } finally {
      setDownloading(false);
    }
  };

  const state = pkg?.state || 'NONE';
  const running = state === 'QUEUED' || state === 'RUNNING';
  return (
    <section className="android-package-panel" aria-live="polite">
      <div className="android-package-head">
        <strong>{fromWebsite ? 'אפליקציית אנדרואיד מהאתר' : 'אפליקציית אנדרואיד (APK/AAB)'}</strong>
        <span className={`android-package-state is-${state.toLowerCase()}`}>
          {running && <span className="package-live-dot" aria-hidden="true"><span className="package-live-dot-core" /><span className="package-live-dot-ring" /></span>}
          {androidPackageLabel(state)}
        </span>
      </div>
      {state === 'FAILED' && <p className="android-package-error">{androidFailureHint(pkg?.errorCode)}</p>}
      {state === 'FAILED' && pkg?.detail && <pre className="android-package-detail">{pkg.detail}</pre>}
      <div className="android-package-actions">
        {state === 'READY' && <button type="button" className="btn submit-btn" onClick={download} disabled={downloading}>{downloading ? 'מכין הורדה...' : 'הורד חבילת אנדרואיד'}</button>}
        {!running && <button type="button" className="btn" onClick={start} disabled={busy}>{busy ? 'מתחיל...' : state === 'READY' || state === 'FAILED' ? 'בנה שוב' : fromWebsite ? 'צור אפליקציית אנדרואיד' : 'בנה APK לאנדרואיד'}</button>}
      </div>
      <small>{fromWebsite
        ? 'האתר שנבנה נארז לאפליקציה בבנייה נפרדת (כ־5 דקות); האתר והתצוגה שלו לא משתנים. הקובץ להתקנה ובדיקה בטלפון; חתימה לפרסום ב־Google Play תתווסף בשלב הבא.'
        : 'התצוגה המקדימה נשארת כפי שהיא. החבילה עוברת בנייה ובדיקה מלאה לפני שהיא זמינה להורדה; חתימה לפרסום ב־Google Play אינה כלולה.'}</small>
      {error && <p className="android-package-error" role="alert">{error}</p>}
    </section>
  );
}
